import React, { useState, useEffect } from 'react';
import {
  Settings,
  User,
  Mail,
  Phone,
  MapPin,
  Globe,
  Bell,
  Clock,
  DollarSign,
  Shield,
  Users,
  FolderOpen,
  Target,
  Save,
  RotateCcw,
  AlertCircle,
  CheckCircle,
  Plus,
  Trash2,
  Edit3,
  Eye,
  EyeOff,
  Building,
  Calendar,
  MessageSquare,
  PencilIcon,
  Zap,
  Lock,
  Tag,
  Info
} from 'lucide-react';
import { DocumentTextIcon } from '@heroicons/react/24/outline';
import api from '../utils/api';
import FormBuilder from './FormBuilder/FormBuilder';
import ClientScopesSection from './ClientScopesSection';
import { RecurringTemplatesList } from './RecurringTemplates';


const ClientSettings = ({ client, user, onClose, onClientUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // State for all settings sections
  const [generalInfo, setGeneralInfo] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    company: '',
    address: '',
    description: ''
  });

  const [customFields, setCustomFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [customFieldMeta, setCustomFieldMeta] = useState({});
  const [revealedSensitiveFields, setRevealedSensitiveFields] = useState({});
  const [sensitivePlainLoaded, setSensitivePlainLoaded] = useState(false);
  const [customVariables, setCustomVariables] = useState([]);

  const [communicationPrefs, setCommunicationPrefs] = useState({
    email_notifications: true,
    preferred_contact_method: 'email',
    timezone: 'UTC',
    best_contact_time: '09:00-17:00',
    language: 'en',
    notification_frequency: 'real-time'
  });

  const [projectDefaults, setProjectDefaults] = useState({
    default_project_template: '',
    default_task_assignee: '',
    default_billing_rate: '',
    default_currency: 'USD',
    default_project_status: 'open',
    auto_assign_tasks: false,
    require_approval: false
  });

  const [portalAccess, setPortalAccess] = useState({
    portal_enabled: false,
    can_view_projects: true,
    can_view_tasks: true,
    can_view_time_entries: false,
    can_comment: true,
    can_approve_deliverables: false,
    client_login_email: '',
    access_expires_at: null,
    access_token: '',
    last_login: null
  });

  const [teamContacts, setTeamContacts] = useState([]);
  const [originalData, setOriginalData] = useState({});
  const [selectedContactForPreview, setSelectedContactForPreview] = useState('');

  const isOwnerOrAdmin = ['owner', 'admin'].includes(String(user?.role || '').toLowerCase());
  const getContextTeamId = () => client?.team_id || client?.teamId || user?.current_team_id || user?.team_id || user?.teamId;

  // State for allowed request types
  const [allowedRequestTypes, setAllowedRequestTypes] = useState({
    allowed: [],           // Current client's allowed types
    companyTypes: [],      // All company request types (master list)
    usesAll: true          // Whether client uses all company types
  });

  // State for portal customization
  const [portalCustomization, setPortalCustomization] = useState({
    auto_generate_tasks: true,
    form_config: null
  });
  const [showFormBuilder, setShowFormBuilder] = useState(false);

  // State for AI Task Generation settings
  const [aiTaskSettings, setAiTaskSettings] = useState({
    inherit_company_settings: true,
    auto_approve_high_confidence: false,
    auto_approve_threshold: 0.85,
    require_approval_for_all: true,
    notification_preferences: {
      email_on_generation: true,
      email_on_approval_needed: true
    }
  });
  const [companyAiSettings, setCompanyAiSettings] = useState(null);
  
  // Available project templates and team members
  const [projectTemplates, setProjectTemplates] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Assignment settings (Phase 2)
  const [assignmentSettings, setAssignmentSettings] = useState({
    default_assignee_id: '',
    account_manager_id: '',
    project_manager_id: '',
    assignment_preferences: {
      routing_rules: {
        high_priority_to: '',
        support_to: ''
      }
    }
  });

  // Data tables (reference-table subscriptions + connector config)
  const [dataTables, setDataTables] = useState([]);
  const [dataTableConfigs, setDataTableConfigs] = useState({});
  const [savingDataTables, setSavingDataTables] = useState(false);
  const [dataTablePreviews, setDataTablePreviews] = useState({});

  const tabs = [
    { id: 'general', name: 'General Information', icon: User },
    { id: 'custom', name: 'Custom Fields', icon: Target },
    { id: 'assignment', name: 'Auto-Assignment', icon: Users },
    { id: 'scopes', name: 'Project Scopes', icon: Target },
    { id: 'communication', name: 'Communication', icon: MessageSquare },
    { id: 'projects', name: 'Project Defaults', icon: FolderOpen },
    { id: 'portal', name: 'Portal Customization', icon: Globe },
    { id: 'data-tables', name: 'Data Tables', icon: Tag },
    { id: 'ai-tasks', name: 'AI Task Generation', icon: Zap },
    { id: 'recurring', name: 'Recurring Tasks', icon: RotateCcw },
    { id: 'contacts', name: 'Team & Contacts', icon: Users }
  ];

  useEffect(() => {
    if (client) {
      loadClientSettings();
    }
  }, [client]);

  const loadClientSettings = async () => {
    setLoading(true);
    setSensitivePlainLoaded(false);
    setRevealedSensitiveFields({});
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        setErrors({ load: 'Authentication required' });
        return;
      }

      // Load client basic info
      setGeneralInfo({
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        website: client.website || '',
        company: client.company || '',
        address: client.headquarters || '',
        description: client.description || ''
      });

      // Load custom field definitions and values
      await Promise.all([
        loadCustomFields(token),
        loadProjectTemplates(token),
        loadTeamMembers(token),
        loadClientContacts(token),
        loadPortalAccess(token),  // Load client portal access
        loadPortalCustomization(token),  // Load portal customization settings
        loadAllowedRequestTypes(token),  // Load client allowed request types
        loadAiTaskSettings(token),  // Load AI task generation settings
        loadAssignmentSettings(token),  // Load assignment settings (Phase 2)
        loadDataTables(token)
      ]);

      // Initialize original data for change detection
      setOriginalData({
        generalInfo: { ...generalInfo },
        communicationPrefs: { ...communicationPrefs },
        projectDefaults: { ...projectDefaults },
        portalAccess: { ...portalAccess }
      });

    } catch (error) {
      console.error('Error loading client settings:', error);
      setErrors({ load: 'Failed to load client settings. Please refresh the page.' });
    } finally {
      setLoading(false);
    }
  };

  const loadCustomFields = async (token, includeSensitive = false) => {
    try {
      // Load field definitions
      const fieldsResponse = await api.get(`/custom-field-definitions?entity_type=client`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomFields(fieldsResponse.data || []);

      // Load client custom field values
      const valuesResponse = await api.get(`/clients/${client.id}/custom-fields`, {
        params: { include_sensitive: includeSensitive },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fieldValues = {};
      const fieldMeta = {};
      (valuesResponse.data?.custom_fields || []).forEach(field => {
        fieldValues[field.field_id] = field.value;
        fieldMeta[field.field_id] = {
          isSensitive: !!field.is_sensitive,
          isMasked: !!field.is_masked,
          canViewSensitive: !!field.can_view_sensitive,
          canEditSensitive: !!field.can_edit_sensitive,
        };
      });
      setCustomFieldValues(fieldValues);
      setCustomFieldMeta(fieldMeta);
      if (includeSensitive) {
        setSensitivePlainLoaded(true);
      }

      // Load custom variables (tenant-level)
      const variablesResponse = await api.get(`/custom-values`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomVariables(variablesResponse.data || []);

    } catch (error) {
      console.error('Error loading custom fields:', error);
    }
  };

  const loadProjectTemplates = async (token) => {
    try {
      const response = await api.get(`/templates/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectTemplates(response.data || []);
    } catch (error) {
      console.error('Error loading project templates:', error);
    }
  };

  const loadTeamMembers = async (token) => {
    try {
      const response = await api.get(`/team/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(response.data || []);
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };


  const loadAssignmentSettings = async (token) => {
    try {
      const response = await api.get(
        `/clients/${client.id}/assignment-settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAssignmentSettings(response.data || {
        default_assignee_id: '',
        account_manager_id: '',
        project_manager_id: '',
        assignment_preferences: { routing_rules: {} }
      });
    } catch (error) {
      console.error('Error loading assignment settings:', error);
    }
  };


  const loadClientContacts = async (token) => {
    try {
      // Fetch ALL contacts for this client from backend
      const contactsResponse = await api.get(
        `/clients/${client.id}/contacts`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const contacts = contactsResponse.data || [];
      
      // Fetch portal access status for each contact
      if (contacts.length > 0) {
        try {
          const response = await api.get(
            `/portal/access/${client.id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          // Map portal contacts by ID for easy lookup
          const portalContactsMap = {};
          if (response.data && response.data.portal_contacts) {
            response.data.portal_contacts.forEach(pc => {
              portalContactsMap[pc.id] = {
                has_portal_access: pc.portal_enabled || false,
                email_verified: pc.email_verified || false,
                last_login: pc.last_login
              };
            });
          }
          
          // Merge portal access status with contacts
          const contactsWithPortalStatus = contacts.map(contact => ({
            ...contact,
            has_portal_access: portalContactsMap[contact.id]?.has_portal_access || false,
            email_verified: portalContactsMap[contact.id]?.email_verified || false,
            last_login: portalContactsMap[contact.id]?.last_login || null
          }));
          
          setTeamContacts(contactsWithPortalStatus);
        } catch (error) {
          console.error('Error loading portal access status:', error);
          // Set contacts without portal status
          setTeamContacts(contacts);
        }
      } else {
        setTeamContacts(contacts);
      }
    } catch (error) {
      console.error('Error loading client contacts:', error);
      setTeamContacts([]);
    }
  };

  const loadPortalAccess = async (token) => {
    try {
      // Load client-specific portal access settings
      const response = await api.get(`/portal/access/${client.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setPortalAccess({
          portal_enabled: response.data.portal_enabled || false,
          primary_contact: response.data.primary_contact || null,
          portal_contacts: response.data.portal_contacts || []
        });
      }
    } catch (error) {
      console.error('Error loading portal access:', error);
      // Set default values if API call fails
      setPortalAccess({
        portal_enabled: false,
        can_view_projects: true,
        can_view_tasks: true,
        can_view_time_entries: false,
        can_comment: true,
        can_approve_deliverables: false,
        client_login_email: client.email || '',
        access_expires_at: null,
        access_token: '',
        last_login: null
      });
    }
  };

  const loadPortalCustomization = async (token) => {
    try {
      // Load client form configuration
      const formConfigResponse = await api.get(
        `/clients/${client.id}/form-config`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Load auto-generate tasks setting (assuming it's stored in client settings)
      const clientSettingsResponse = await api.get(
        `/clients/${client.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setPortalCustomization({
        form_config: formConfigResponse.data || null,
        auto_generate_tasks: clientSettingsResponse.data?.auto_generate_tasks ?? true
      });
      
    } catch (error) {
      console.error('Error loading portal customization:', error);
      // Set defaults if API calls fail
      setPortalCustomization({
        form_config: null,
        auto_generate_tasks: true
      });
    }
  };

  const loadAllowedRequestTypes = async (token) => {
    try {
      const response = await api.get(
        `/clients/${client.id}/allowed-request-types`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data) {
        setAllowedRequestTypes({
          allowed: response.data.allowed_request_types || [],
          companyTypes: response.data.company_request_types || [],
          usesAll: response.data.uses_all_company_types !== false
        });
      }
    } catch (error) {
      console.error('Error loading allowed request types:', error);
      // Set defaults if API call fails
      setAllowedRequestTypes({
        allowed: [],
        companyTypes: [],
        usesAll: true
      });
    }
  };

  const loadAiTaskSettings = async (token) => {
    try {
      // Load both company and client AI task generation settings
      const response = await api.get(
        `/ai-task-generation/settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // Set company settings for display
        setCompanyAiSettings(response.data.company_settings || {});
        
        // Find client-specific settings
        const clientSettings = response.data.client_settings?.find(
          cs => cs.client_id === client.id
        );
        
        if (clientSettings) {
          setAiTaskSettings({
            inherit_company_settings: clientSettings.inherit_company_settings ?? true,
            auto_approve_high_confidence: clientSettings.auto_approve_high_confidence ?? false,
            auto_approve_threshold: clientSettings.auto_approve_threshold ?? 0.85,
            require_approval_for_all: clientSettings.require_approval_for_all ?? true,
            notification_preferences: {
              email_on_generation: clientSettings.notification_preferences?.email_on_generation ?? true,
              email_on_approval_needed: clientSettings.notification_preferences?.email_on_approval_needed ?? true
            }
          });
        } else {
          // No client-specific settings found, use defaults (inherit company settings)
          setAiTaskSettings({
            inherit_company_settings: true,
            auto_approve_high_confidence: false,
            auto_approve_threshold: 0.85,
            require_approval_for_all: true,
            notification_preferences: {
              email_on_generation: true,
              email_on_approval_needed: true
            }
          });
        }
      }
      
    } catch (error) {
      console.error('Error loading AI task settings:', error);
      // Set defaults if API call fails
      setAiTaskSettings({
        inherit_company_settings: true,
        auto_approve_high_confidence: false,
        auto_approve_threshold: 0.85,
        require_approval_for_all: true,
        notification_preferences: {
          email_on_generation: true,
          email_on_approval_needed: true
        }
      });
    }
  };

  const loadDataTables = async (token) => {
    try {
      const teamId = getContextTeamId();
      if (!teamId || !client?.id) {
        setDataTables([]);
        setDataTableConfigs({});
        return;
      }

      const [tablesRes, subsRes] = await Promise.all([
        api.get(`/agent/reference-tables/${teamId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get(`/agent/reference-tables/${teamId}/subscriptions/${client.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const tables = Array.isArray(tablesRes.data) ? tablesRes.data : [];
      const cfgMap = {};
      (subsRes.data?.table_configs || []).forEach((cfg) => {
        if (cfg?.table_id) cfgMap[String(cfg.table_id)] = cfg;
      });

      setDataTables(tables);
      setDataTableConfigs(cfgMap);
    } catch (error) {
      console.error('Error loading data tables:', error);
      setDataTables([]);
      setDataTableConfigs({});
    }
  };

  const updateDataTableConfig = (tableId, patch) => {
    const key = String(tableId);
    setDataTableConfigs(prev => {
      const current = prev[key] || {
        table_id: key,
        is_subscribed: false,
        data_method: 'manual_upload',
        method_config: {},
        is_live: false
      };
      return { ...prev, [key]: { ...current, ...patch } };
    });
  };

  const isSeoTable = (tableName = '') => {
    const n = String(tableName || '').toLowerCase();
    return n.includes('keyword') || n.includes('competitor') || n.includes('rank') || n.includes('overlap');
  };

  const getManagedDataMethod = (tableName = '') => {
    return isSeoTable(tableName) ? 'ahrefs' : 'website_scrape';
  };

  const saveDataTables = async () => {
    setSavingDataTables(true);
    try {
      const token = localStorage.getItem('access_token');
      const teamId = getContextTeamId();
      if (!teamId || !client?.id) return;

      const tableConfigs = dataTables.map((t) => {
        const key = String(t.id);
        const cfg = dataTableConfigs[key] || {};
        const existingMethodConfig = cfg.method_config || {};
        const method_config = isSeoTable(t.name)
          ? { api_key: String(existingMethodConfig.api_key || '').trim() }
          : {};
        return {
          table_id: key,
          is_subscribed: !!cfg.is_subscribed,
          data_method: getManagedDataMethod(t.name),
          method_config,
          is_live: !!cfg.is_subscribed
        };
      });
      const subscribedIds = tableConfigs.filter(c => c.is_subscribed).map(c => c.table_id);

      await api.put(`/agent/reference-tables/${teamId}/subscriptions/${client.id}`, {
        subscribed_table_ids: subscribedIds,
        table_configs: tableConfigs
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2500);
    } catch (error) {
      console.error('Error saving data table settings:', error);
      setErrors(prev => ({ ...prev, save: 'Failed to save data table settings' }));
    } finally {
      setSavingDataTables(false);
    }
  };

  const refreshDataTable = async (tableId) => {
    try {
      const token = localStorage.getItem('access_token');
      const teamId = getContextTeamId();
      if (!teamId || !client?.id) return;
      await api.post(`/agent/reference-tables/${teamId}/subscriptions/${client.id}/${tableId}/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadDataTables(token);
      await loadDataTablePreview(tableId);
    } catch (error) {
      console.error('Error refreshing data table:', error);
      setErrors(prev => ({ ...prev, save: 'Failed to refresh table' }));
    }
  };

  const loadDataTablePreview = async (tableId) => {
    try {
      const token = localStorage.getItem('access_token');
      const teamId = getContextTeamId();
      if (!teamId || !client?.id) return;
      const response = await api.get(`/agent/reference-tables/${teamId}/subscriptions/${client.id}/${tableId}/preview?limit=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDataTablePreviews(prev => ({ ...prev, [String(tableId)]: response.data }));
    } catch (error) {
      console.error('Error loading data table preview:', error);
      setDataTablePreviews(prev => ({ ...prev, [String(tableId)]: { rows: [], columns: [], error: 'Failed to load preview' } }));
    }
  };

  const handleGeneralInfoChange = (field, value) => {
    setGeneralInfo(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
    clearFieldError(field);
  };

  const handleCustomFieldChange = (fieldId, value) => {
    setCustomFieldValues(prev => ({ ...prev, [fieldId]: value }));
    setCustomFieldMeta(prev => ({
      ...prev,
      [fieldId]: {
        ...(prev[fieldId] || {}),
        isMasked: false,
      }
    }));
    setHasUnsavedChanges(true);
  };

  const getCustomFieldOptions = (field) => {
    const raw = field?.options;
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      } catch (_) {
        return raw
          .split(',')
          .map(v => v.trim())
          .filter(Boolean);
      }
    }
    return [];
  };

  const normalizeCustomFieldArrayValue = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
      } catch (_) {
        return trimmed.split(',').map(v => v.trim()).filter(Boolean);
      }
    }
    return [];
  };

  const isSensitiveField = (fieldId) => !!customFieldMeta[fieldId]?.isSensitive;
  const isMaskedSensitiveField = (fieldId) => !!customFieldMeta[fieldId]?.isMasked;
  const canViewSensitiveField = (fieldId) => !!customFieldMeta[fieldId]?.canViewSensitive;

  const handleRevealSensitiveField = async (fieldId) => {
    if (!isOwnerOrAdmin || !canViewSensitiveField(fieldId)) return;
    try {
      if (!sensitivePlainLoaded) {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        await loadCustomFields(token, true);
      }
      setRevealedSensitiveFields(prev => ({ ...prev, [fieldId]: true }));
    } catch (error) {
      console.error('Error revealing sensitive field:', error);
      setErrors(prev => ({ ...prev, save: 'Failed to reveal sensitive field' }));
    }
  };

  const handleHideSensitiveField = (fieldId) => {
    setRevealedSensitiveFields(prev => ({ ...prev, [fieldId]: false }));
  };

  const handleCommunicationChange = (field, value) => {
    setCommunicationPrefs(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handleProjectDefaultsChange = (field, value) => {
    setProjectDefaults(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const handlePortalAccessChange = (field, value) => {
    setPortalAccess(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const clearFieldError = (field) => {
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    if (saveSuccess) {
      setSaveSuccess(false);
    }
  };

  const validateGeneralInfo = () => {
    const newErrors = {};
    
    if (!generalInfo.name.trim()) {
      newErrors.name = 'Client name is required';
    }
    
    if (generalInfo.email && !isValidEmail(generalInfo.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (generalInfo.website && !isValidUrl(generalInfo.website)) {
      newErrors.website = 'Please enter a valid website URL';
    }
    
    return newErrors;
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidUrl = (url) => {
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      return true;
    } catch {
      return false;
    }
  };


  const handleSaveAssignment = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        setErrors({ save: 'Authentication error. Please log in again.' });
        return;
      }

      await api.put(
        `/clients/${client.id}/assignment-settings`,
        assignmentSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving assignment settings:', error);
      setErrors({ save: error.response?.data?.detail || 'Failed to save assignment settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const validationErrors = validateGeneralInfo();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        setErrors({ save: 'Authentication error. Please log in again.' });
        return;
      }

      // Update client basic information
      const clientUpdateData = {
        name: generalInfo.name,
        email: generalInfo.email,
        phone: generalInfo.phone,
        website: generalInfo.website,
        company: generalInfo.company,
        headquarters: generalInfo.address,
        description: generalInfo.description,
        contacts: teamContacts
      };

      const clientResponse = await api.put(`/clients/${client.id}`, clientUpdateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update custom field values (client-only fields), tolerate per-field failures and surface them.
      const allowedFieldIds = new Set((customFields || []).map(f => String(f.id)));
      const customFieldEntries = Object.entries(customFieldValues).filter(([fieldId, value]) => {
        if (!allowedFieldIds.has(String(fieldId))) return false;
        // Do not re-save masked placeholder values; backend keeps existing secret.
        if (isSensitiveField(fieldId) && isMaskedSensitiveField(fieldId) && value === '****') return false;
        return true;
      });

      const customFieldResults = await Promise.allSettled(
        customFieldEntries.map(([fieldId, value]) => {
          return api.put(`/clients/${client.id}/custom-fields/${fieldId}`, {
            value: value
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        })
      );

      const failedCustomFieldUpdates = customFieldResults.filter(r => r.status === 'rejected');
      if (failedCustomFieldUpdates.length > 0) {
        throw new Error(`Failed to save ${failedCustomFieldUpdates.length} custom field value(s)`);
      }

      // Update portal access settings (always send, even if disabled)
      await api.put(`/portal/access/${client.id}`, {
        portal_enabled: portalAccess.portal_enabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update original data to reflect saved state
      setOriginalData({
        generalInfo: { ...generalInfo },
        communicationPrefs: { ...communicationPrefs },
        projectDefaults: { ...projectDefaults },
        portalAccess: { ...portalAccess }
      });

      setSaveSuccess(true);
      setHasUnsavedChanges(false);
      
      // Notify parent component of client update
      if (onClientUpdate) {
        onClientUpdate(clientResponse.data);
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);

    } catch (error) {
      console.error('Error saving client settings:', error);
      
      // Handle different error response formats
      let errorMessage = 'Failed to save client settings. Please try again.';
      
      if (error.response?.data) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // FastAPI validation error format
          errorMessage = error.response.data.detail.map(err => 
            err.msg || JSON.stringify(err)
          ).join(', ');
        } else if (typeof error.response.data.error === 'string') {
          errorMessage = error.response.data.error;
        }
      }
      
      setErrors({ save: errorMessage });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setGeneralInfo(originalData.generalInfo || {});
    setCommunicationPrefs(originalData.communicationPrefs || {});
    setProjectDefaults(originalData.projectDefaults || {});
    setPortalAccess(originalData.portalAccess || {});
    setErrors({});
    setSaveSuccess(false);
    setHasUnsavedChanges(false);
    setRevealedSensitiveFields({});
  };

  const addContact = () => {
    const newContact = {
      id: Date.now().toString(), // Temporary ID
      name: '',
      email: '',
      phone: '',
      job_title: '',
      department: '',
      is_primary: teamContacts.length === 0
    };
    setTeamContacts([...teamContacts, newContact]);
  };

  const updateContact = (contactId, field, value) => {
    setTeamContacts(prev => prev.map(contact => 
      contact.id === contactId 
        ? { ...contact, [field]: value }
        : contact
    ));
    setHasUnsavedChanges(true);
  };

  const removeContact = (contactId) => {
    setTeamContacts(prev => prev.filter(contact => contact.id !== contactId));
  };

  const hasChanges = () => {
    return hasUnsavedChanges || (
      JSON.stringify(generalInfo) !== JSON.stringify(originalData.generalInfo) ||
      JSON.stringify(communicationPrefs) !== JSON.stringify(originalData.communicationPrefs) ||
      JSON.stringify(projectDefaults) !== JSON.stringify(originalData.projectDefaults) ||
      JSON.stringify(portalAccess) !== JSON.stringify(originalData.portalAccess)
    );
  };

  const handleViewPortal = async () => {
    if (!portalAccess.portal_enabled) {
      alert('Portal access must be enabled first');
      return;
    }

    try {
      // Call the new admin login-as endpoint
      const response = await api.get(
        `/admin/portal/login-as/${client.id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );

      if (response.data.success) {
        // Pass token via URL hash (more secure than query params)
        const portalData = {
          token: response.data.token,
          team_id: response.data.team_id,
          contact: response.data.contact,
          client: response.data.client
        };
        
        // Encode data as base64 to pass in URL hash
        const encodedData = btoa(JSON.stringify(portalData));
        const portalUrl = `${window.location.origin}/portal/${response.data.team_id}/dashboard#auth=${encodedData}`;
        
        // Open in new tab
        window.open(portalUrl, '_blank', 'noopener,noreferrer');
      } else {
        // Show error message
        const errorMsg = typeof response.data.error === 'string' 
          ? response.data.error 
          : 'Unable to access portal. Please check portal settings.';
        alert(errorMsg);
      }
    } catch (error) {
      console.error('Error accessing portal:', error);
      
      // Handle different error types
      let errorMessage = 'Failed to access portal. Please try again.';
      
      if (error.response?.data) {
        if (typeof error.response.data.error === 'string') {
          errorMessage = error.response.data.error;
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // FastAPI validation error format
          errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
        }
      }
      
      alert(errorMessage);
    }
  };

  // Portal customization handlers
  const handleOpenFormBuilder = () => {
    setShowFormBuilder(true);
  };

  const handleSaveClientForm = async (formData) => {
    try {
      const response = await api.post(
        `/clients/${client.id}/form-config`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );
      
      setPortalCustomization(prev => ({
        ...prev,
        form_config: response.data
      }));
      
      setShowFormBuilder(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
    } catch (error) {
      console.error('Error saving client form:', error);
      setErrors({
        formBuilder: error.response?.data?.detail || 'Failed to save form configuration'
      });
    }
  };

  const handleToggleAutoGenerateTasks = async (enabled) => {
    try {
      await api.put(
        `/clients/${client.id}/portal-settings`,
        { auto_generate_tasks: enabled },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );
      
      setPortalCustomization(prev => ({
        ...prev,
        auto_generate_tasks: enabled
      }));
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
    } catch (error) {
      console.error('Error updating auto generate tasks setting:', error);
      setErrors({
        autoTasks: error.response?.data?.detail || 'Failed to update auto-generate tasks setting'
      });
    }
  };

  const handleAiTaskSettingsChange = (field, value) => {
    setAiTaskSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAiTaskNotificationChange = (field, value) => {
    setAiTaskSettings(prev => ({
      ...prev,
      notification_preferences: {
        ...prev.notification_preferences,
        [field]: value
      }
    }));
  };

  const saveAiTaskSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('access_token');
      
      await api.put(
        `/ai-task-generation/settings/client/${client.id}`,
        aiTaskSettings,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
    } catch (error) {
      console.error('Error saving AI task settings:', error);
      setErrors({
        aiTasks: error.response?.data?.detail || 'Failed to save AI task generation settings'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAllowedRequestTypeToggle = async (requestType, isChecked) => {
    try {
      const token = localStorage.getItem('access_token');
      
      let newAllowed;
      if (allowedRequestTypes.usesAll) {
        // Switching from "use all" to specific types - start with all company types minus the unchecked one
        if (isChecked) {
          newAllowed = [...allowedRequestTypes.companyTypes];
        } else {
          newAllowed = allowedRequestTypes.companyTypes.filter(t => t !== requestType);
        }
      } else {
        // Already using specific types
        if (isChecked) {
          newAllowed = [...allowedRequestTypes.allowed, requestType];
        } else {
          newAllowed = allowedRequestTypes.allowed.filter(t => t !== requestType);
        }
      }
      
      await api.put(
        `/clients/${client.id}/allowed-request-types`,
        { request_types: newAllowed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAllowedRequestTypes({
        ...allowedRequestTypes,
        allowed: newAllowed,
        usesAll: false
      });
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      
    } catch (error) {
      console.error('Error updating allowed request types:', error);
      setErrors({
        requestTypes: error.response?.data?.detail || 'Failed to update allowed request types'
      });
    }
  };

  const handleResetToAllRequestTypes = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      await api.put(
        `/clients/${client.id}/allowed-request-types`,
        { request_types: null },  // null means use all company types
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setAllowedRequestTypes({
        ...allowedRequestTypes,
        allowed: allowedRequestTypes.companyTypes,
        usesAll: true
      });
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
      
    } catch (error) {
      console.error('Error resetting allowed request types:', error);
      setErrors({
        requestTypes: error.response?.data?.detail || 'Failed to reset allowed request types'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading client settings...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading client settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <Settings className="w-6 h-6 text-purple-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Client Settings</h2>
            <p className="text-gray-600">{client.name}</p>
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {saveSuccess && (
        <div className="mx-6 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <p className="text-green-800 font-medium">Client settings updated successfully!</p>
          </div>
        </div>
      )}

      {errors.load && (
        <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800">{errors.load}</p>
          </div>
        </div>
      )}

      {errors.save && (
        <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <p className="text-red-800">{errors.save}</p>
          </div>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="w-80 bg-gray-50 border-r border-gray-200 overflow-y-auto">
          <nav className="p-4 space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-purple-100 text-purple-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6">
              {/* General Information Tab */}
              {activeTab === 'general' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <User className="w-4 h-4 inline mr-1" />
                          Client Name *
                        </label>
                        <input
                          type="text"
                          value={generalInfo.name}
                          onChange={(e) => handleGeneralInfoChange('name', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                            errors.name ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="Enter client name"
                        />
                        {errors.name && (
                          <p className="text-red-600 text-sm mt-1">{errors.name}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Building className="w-4 h-4 inline mr-1" />
                          Company
                        </label>
                        <input
                          type="text"
                          value={generalInfo.company}
                          onChange={(e) => handleGeneralInfoChange('company', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Company name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Mail className="w-4 h-4 inline mr-1" />
                          Email
                        </label>
                        <input
                          type="email"
                          value={generalInfo.email}
                          onChange={(e) => handleGeneralInfoChange('email', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                            errors.email ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="client@example.com"
                        />
                        {errors.email && (
                          <p className="text-red-600 text-sm mt-1">{errors.email}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Phone className="w-4 h-4 inline mr-1" />
                          Phone
                        </label>
                        <input
                          type="tel"
                          value={generalInfo.phone}
                          onChange={(e) => handleGeneralInfoChange('phone', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Globe className="w-4 h-4 inline mr-1" />
                          Website
                        </label>
                        <input
                          type="url"
                          value={generalInfo.website}
                          onChange={(e) => handleGeneralInfoChange('website', e.target.value)}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                            errors.website ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="https://example.com"
                        />
                        {errors.website && (
                          <p className="text-red-600 text-sm mt-1">{errors.website}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Address & Description</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <MapPin className="w-4 h-4 inline mr-1" />
                          Address
                        </label>
                        <textarea
                          value={generalInfo.address}
                          onChange={(e) => handleGeneralInfoChange('address', e.target.value)}
                          rows={3}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-vertical"
                          placeholder="123 Business Street&#10;Suite 100&#10;City, State 12345"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Description
                        </label>
                        <textarea
                          value={generalInfo.description}
                          onChange={(e) => handleGeneralInfoChange('description', e.target.value)}
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-vertical"
                          placeholder="Brief description of the client and their business..."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Custom Fields Tab */}
              {activeTab === 'custom' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Custom Client Fields</h3>
                    <p className="text-gray-600 mb-6">
                      Configure client-specific values for your organization's custom fields.
                    </p>
                  </div>

                  {customFields.length > 0 ? (
                    <div className="space-y-4">
                      {customFields.map((field) => {
                        const sensitive = isSensitiveField(field.id);
                        const revealed = !!revealedSensitiveFields[field.id];
                        const canReveal = isOwnerOrAdmin && canViewSensitiveField(field.id);
                        const disableSensitiveInput = sensitive && (!canReveal || !revealed);
                        const normalizedType = String(field.type || 'text').toLowerCase();
                        const fieldOptions = getCustomFieldOptions(field);

                        return (
                        <div key={field.id}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {field.label}
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          {field.description && (
                            <p className="text-sm text-gray-500 mb-2">{field.description}</p>
                          )}
                          
                          {(normalizedType === 'text' || normalizedType === 'textarea') && (
                            <input
                              type={sensitive ? (revealed ? 'text' : 'password') : 'text'}
                              value={customFieldValues[field.id] || ''}
                              onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                              disabled={disableSensitiveInput}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                              placeholder={`Enter ${field.label.toLowerCase()}`}
                            />
                          )}
                          
                          {(['select', 'dropdown', 'single_option', 'single-select'].includes(normalizedType)) && (
                            <select
                              value={customFieldValues[field.id] || ''}
                              onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                              disabled={disableSensitiveInput}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                            >
                              <option value="">Select {field.label}</option>
                              {fieldOptions.map((option) => (
                                <option key={option} value={option}>{option}</option>
                              ))}
                            </select>
                          )}

                          {(['multiselect', 'multi_select', 'multiple_option', 'multi-option'].includes(normalizedType)) && (
                            <div className={`w-full border border-gray-300 rounded-lg p-3 space-y-2 ${disableSensitiveInput ? 'bg-gray-100 text-gray-500' : 'bg-white'}`}>
                              {fieldOptions.length > 0 ? fieldOptions.map((option) => {
                                const selected = normalizeCustomFieldArrayValue(customFieldValues[field.id]);
                                const checked = selected.includes(option);
                                return (
                                  <label key={option} className={`flex items-center gap-2 text-sm ${disableSensitiveInput ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      disabled={disableSensitiveInput}
                                      onChange={(e) => {
                                        const current = normalizeCustomFieldArrayValue(customFieldValues[field.id]);
                                        const next = e.target.checked
                                          ? Array.from(new Set([...current, option]))
                                          : current.filter(v => v !== option);
                                        handleCustomFieldChange(field.id, next);
                                      }}
                                      className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                                    />
                                    <span>{option}</span>
                                  </label>
                                );
                              }) : (
                                <p className="text-sm text-gray-500">No options configured for this field.</p>
                              )}
                            </div>
                          )}
                          
                          {normalizedType === 'email' && (
                            <input
                              type={sensitive ? (revealed ? 'text' : 'password') : 'email'}
                              value={customFieldValues[field.id] || ''}
                              onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                              disabled={disableSensitiveInput}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                              placeholder={`Enter ${field.label.toLowerCase()}`}
                            />
                          )}
                          
                          {(normalizedType === 'phone' || normalizedType === 'tel') && (
                            <input
                              type={sensitive ? (revealed ? 'text' : 'password') : 'tel'}
                              value={customFieldValues[field.id] || ''}
                              onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                              disabled={disableSensitiveInput}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                              placeholder={`Enter ${field.label.toLowerCase()}`}
                            />
                          )}
                          
                          {normalizedType === 'url' && (
                            <input
                              type={sensitive ? (revealed ? 'text' : 'password') : 'url'}
                              value={customFieldValues[field.id] || ''}
                              onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                              disabled={disableSensitiveInput}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                              placeholder={`Enter ${field.label.toLowerCase()}`}
                            />
                          )}

                          {!['text', 'textarea', 'select', 'dropdown', 'single_option', 'single-select', 'multiselect', 'multi_select', 'multiple_option', 'multi-option', 'email', 'phone', 'tel', 'url'].includes(normalizedType) && (
                            <input
                              type={sensitive ? (revealed ? 'text' : 'password') : 'text'}
                              value={customFieldValues[field.id] || ''}
                              onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                              disabled={disableSensitiveInput}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500"
                              placeholder={`Enter ${field.label.toLowerCase()}`}
                            />
                          )}
                          
                          {sensitive && (
                            <div className="mt-2 flex items-center gap-2">
                              {canReveal && !revealed && (
                                <button
                                  type="button"
                                  onClick={() => handleRevealSensitiveField(field.id)}
                                  className="inline-flex items-center px-3 py-1.5 text-xs rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                                >
                                  <Eye className="w-3 h-3 mr-1" /> Reveal
                                </button>
                              )}
                              {canReveal && revealed && (
                                <button
                                  type="button"
                                  onClick={() => handleHideSensitiveField(field.id)}
                                  className="inline-flex items-center px-3 py-1.5 text-xs rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                                >
                                  <EyeOff className="w-3 h-3 mr-1" /> Hide
                                </button>
                              )}
                              {!canReveal && (
                                <span className="text-xs text-gray-500">Sensitive field (owner/admin only)</span>
                              )}
                            </div>
                          )}

                          <div className="text-xs text-gray-500 mt-1">
                            Variable: <code className="bg-gray-100 px-1 rounded">{"{{client." + field.variable_name + "}}"}</code>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Target className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Custom Fields</h3>
                      <p className="text-gray-600 mb-4">
                        No custom fields have been defined yet. Create client custom fields in the Advanced Variables settings.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Scopes Tab */}


              {/* Assignment Tab (Phase 2) */}
              {activeTab === 'assignment' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Auto-Assignment Settings</h3>
                    <p className="text-gray-600 mb-6">
                      Configure how tasks from this client are automatically assigned to team members.
                    </p>
                  </div>

                  {/* Primary Contacts */}
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <Users className="w-5 h-5 mr-2 text-purple-600" />
                      Primary Contacts
                    </h4>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Default Task Assignee
                        </label>
                        <select
                          value={assignmentSettings.default_assignee_id || ''}
                          onChange={(e) => setAssignmentSettings({
                            ...assignmentSettings,
                            default_assignee_id: e.target.value
                          })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="">No default (use category or rules)</option>
                          {teamMembers.map(member => (
                            <option key={member.id} value={member.id}>
                              {member.name || member.email}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          All tasks from this client will be automatically assigned to this person unless overridden by priority rules
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Account Manager
                        </label>
                        <select
                          value={assignmentSettings.account_manager_id || ''}
                          onChange={(e) => setAssignmentSettings({
                            ...assignmentSettings,
                            account_manager_id: e.target.value
                          })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="">None</option>
                          {teamMembers.map(member => (
                            <option key={member.id} value={member.id}>
                              {member.name || member.email}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Manages client relationship, receives notifications
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Project Manager
                        </label>
                        <select
                          value={assignmentSettings.project_manager_id || ''}
                          onChange={(e) => setAssignmentSettings({
                            ...assignmentSettings,
                            project_manager_id: e.target.value
                          })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="">None</option>
                          {teamMembers.map(member => (
                            <option key={member.id} value={member.id}>
                              {member.name || member.email}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          Manages all projects for this client
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Advanced Routing Rules */}
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <Target className="w-5 h-5 mr-2 text-purple-600" />
                      Advanced Routing Rules
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Override default assignment based on task priority or type
                    </p>
                    
                    <div className="space-y-4">
                      <div className="flex items-center space-x-3">
                        <label className="w-48 text-sm font-medium text-gray-700">
                          High Priority Tasks →
                        </label>
                        <select
                          value={assignmentSettings.assignment_preferences?.routing_rules?.high_priority_to || ''}
                          onChange={(e) => setAssignmentSettings({
                            ...assignmentSettings,
                            assignment_preferences: {
                              ...assignmentSettings.assignment_preferences,
                              routing_rules: {
                                ...assignmentSettings.assignment_preferences?.routing_rules,
                                high_priority_to: e.target.value
                              }
                            }
                          })}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">Use default assignee</option>
                          {teamMembers.map(member => (
                            <option key={member.id} value={member.id}>
                              {member.name || member.email}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="flex items-center space-x-3">
                        <label className="w-48 text-sm font-medium text-gray-700">
                          Support Requests →
                        </label>
                        <select
                          value={assignmentSettings.assignment_preferences?.routing_rules?.support_to || ''}
                          onChange={(e) => setAssignmentSettings({
                            ...assignmentSettings,
                            assignment_preferences: {
                              ...assignmentSettings.assignment_preferences,
                              routing_rules: {
                                ...assignmentSettings.assignment_preferences?.routing_rules,
                                support_to: e.target.value
                              }
                            }
                          })}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">Use default assignee</option>
                          {teamMembers.map(member => (
                            <option key={member.id} value={member.id}>
                              {member.name || member.email}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Priority Order:</strong> High-priority rules → Client default → Category default → Standard rules → Assignment mode
                      </p>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="flex justify-end space-x-3 pt-4 border-t">
                    <button
                      onClick={handleSaveAssignment}
                      disabled={saving}
                      className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      {saving ? 'Saving...' : 'Save Assignment Settings'}
                    </button>
                  </div>

                  {saveSuccess && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-800 text-sm">✓ Assignment settings saved successfully!</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'scopes' && (
                <div className="space-y-6">
                  <ClientScopesSection clientId={client.id} />
                </div>
              )}

              {/* Communication Preferences Tab */}
              {activeTab === 'communication' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Communication Preferences</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <MessageSquare className="w-4 h-4 inline mr-1" />
                          Preferred Contact Method
                        </label>
                        <select
                          value={communicationPrefs.preferred_contact_method}
                          onChange={(e) => handleCommunicationChange('preferred_contact_method', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="email">Email</option>
                          <option value="phone">Phone</option>
                          <option value="text">Text/SMS</option>
                          <option value="portal">Client Portal</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Clock className="w-4 h-4 inline mr-1" />
                          Timezone
                        </label>
                        <select
                          value={communicationPrefs.timezone}
                          onChange={(e) => handleCommunicationChange('timezone', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="UTC">UTC</option>
                          <option value="America/New_York">Eastern Time</option>
                          <option value="America/Chicago">Central Time</option>
                          <option value="America/Denver">Mountain Time</option>
                          <option value="America/Los_Angeles">Pacific Time</option>
                          <option value="Europe/London">London</option>
                          <option value="Europe/Paris">Paris</option>
                          <option value="Asia/Tokyo">Tokyo</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Best Contact Time
                        </label>
                        <input
                          type="text"
                          value={communicationPrefs.best_contact_time}
                          onChange={(e) => handleCommunicationChange('best_contact_time', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="09:00-17:00"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Notification Frequency
                        </label>
                        <select
                          value={communicationPrefs.notification_frequency}
                          onChange={(e) => handleCommunicationChange('notification_frequency', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="real-time">Real-time</option>
                          <option value="daily">Daily digest</option>
                          <option value="weekly">Weekly summary</option>
                          <option value="none">No notifications</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Settings</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Email Notifications</label>
                          <p className="text-sm text-gray-500">Receive email updates about project progress</p>
                        </div>
                        <button
                          onClick={() => handleCommunicationChange('email_notifications', !communicationPrefs.email_notifications)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                            communicationPrefs.email_notifications ? 'bg-purple-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              communicationPrefs.email_notifications ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Project Defaults Tab */}
              {activeTab === 'projects' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Project & Task Defaults</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <FolderOpen className="w-4 h-4 inline mr-1" />
                          Default Project Template
                        </label>
                        <select
                          value={projectDefaults.default_project_template}
                          onChange={(e) => handleProjectDefaultsChange('default_project_template', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="">No default template</option>
                          {projectTemplates.map((template) => (
                            <option key={template.id} value={template.id}>{template.name}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <User className="w-4 h-4 inline mr-1" />
                          Default Task Assignee
                        </label>
                        <select
                          value={projectDefaults.default_task_assignee}
                          onChange={(e) => handleProjectDefaultsChange('default_task_assignee', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="">No default assignee</option>
                          {teamMembers.map((member) => (
                            <option key={member.id} value={member.id}>{member.name}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <DollarSign className="w-4 h-4 inline mr-1" />
                          Default Billing Rate
                        </label>
                        <input
                          type="number"
                          value={projectDefaults.default_billing_rate}
                          onChange={(e) => handleProjectDefaultsChange('default_billing_rate', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="150"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Currency
                        </label>
                        <select
                          value={projectDefaults.default_currency}
                          onChange={(e) => handleProjectDefaultsChange('default_currency', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="USD">USD ($)</option>
                          <option value="EUR">EUR (€)</option>
                          <option value="GBP">GBP (£)</option>
                          <option value="CAD">CAD (C$)</option>
                          <option value="AUD">AUD (A$)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Workflow Preferences</h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Auto-assign Tasks</label>
                          <p className="text-sm text-gray-500">Automatically assign new tasks to default assignee</p>
                        </div>
                        <button
                          onClick={() => handleProjectDefaultsChange('auto_assign_tasks', !projectDefaults.auto_assign_tasks)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                            projectDefaults.auto_assign_tasks ? 'bg-purple-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              projectDefaults.auto_assign_tasks ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>

                      <div className="flex items-center justify-between">
                        <div>
                          <label className="text-sm font-medium text-gray-700">Require Client Approval</label>
                          <p className="text-sm text-gray-500">Require client approval for task completion</p>
                        </div>
                        <button
                          onClick={() => handleProjectDefaultsChange('require_approval', !projectDefaults.require_approval)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                            projectDefaults.require_approval ? 'bg-purple-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              projectDefaults.require_approval ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Portal Customization Tab */}
              {activeTab === 'portal' && (
                <div className="space-y-8">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Client Portal Settings</h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      Configure portal access, permissions, and customize the client experience
                    </p>
                  </div>

                  {/* SECTION 1: Portal Access Control */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                        <Shield className="w-5 h-5 mr-2" />
                        Portal Access Control
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Enable portal access and configure client login credentials
                      </p>
                    </div>

                    <div className="p-6 space-y-6">
                      {/* Enable Portal Access */}
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Portal Access</label>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Allow this client to access their dedicated portal</p>
                        </div>
                        <button
                          onClick={() => handlePortalAccessChange('portal_enabled', !portalAccess.portal_enabled)}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                            portalAccess.portal_enabled ? 'bg-purple-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              portalAccess.portal_enabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>

                      {portalAccess.portal_enabled && (
                        <>
                          {/* Portal Access Information */}
                          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                            <h5 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                              Portal Access Enabled
                            </h5>
                            <p className="text-sm text-blue-800 dark:text-blue-200 mb-3">
                              Contacts will use their email addresses to log in to the portal. You can grant portal access to individual contacts in the Team & Contacts section below.
                            </p>
                            {portalAccess.primary_contact && (
                              <div className="flex items-center space-x-2 text-sm">
                                <User className="w-4 h-4 text-blue-600" />
                                <span className="text-blue-900 dark:text-blue-100">
                                  Primary Contact: <strong>{portalAccess.primary_contact.name}</strong>
                                  {portalAccess.primary_contact.email && ` (${portalAccess.primary_contact.email})`}
                                </span>
                                {portalAccess.primary_contact.portal_enabled && (
                                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                    Portal Access
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Portal URL Info */}
                          <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                            <div className="flex items-center justify-between">
                              <div>
                                <h5 className="text-sm font-medium text-purple-900 dark:text-purple-100 flex items-center">
                                  <Globe className="w-4 h-4 mr-2" />
                                  Portal Access Info
                                </h5>
                                <p className="text-sm text-purple-800 dark:text-purple-200 mt-1">
                                  <strong>Portal URL:</strong>{' '}
                                  <code className="bg-purple-100 dark:bg-purple-800 px-2 py-1 rounded text-xs break-all">
                                    {window.location.origin}/portal/{client.team_id}
                                  </code>
                                  <button
                                    onClick={() => {
                                      const url = `${window.location.origin}/portal/${client.team_id}`;
                                      navigator.clipboard.writeText(url);
                                      alert('Portal URL copied to clipboard!');
                                    }}
                                    className="ml-2 text-purple-600 hover:text-purple-800 text-xs underline"
                                  >
                                    Copy
                                  </button>
                                </p>
                                {portalAccess.last_login && (
                                  <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                                    Last Login: {new Date(portalAccess.last_login).toLocaleString()}
                                  </p>
                                )}
                              </div>
                              <button
                                onClick={handleViewPortal}
                                disabled={!portalAccess.portal_enabled}
                                className="flex items-center space-x-1 px-3 py-1 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs"
                              >
                                <Eye className="w-3 h-3" />
                                <span>View Portal</span>
                              </button>
                            </div>
                          </div>
                        </>
                      )}

                      {!portalAccess.portal_enabled && (
                        <div className="text-center py-8">
                          <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Portal Access Disabled</h4>
                          <p className="text-gray-600 dark:text-gray-400 mb-4">
                            Enable portal access to allow this client to view their projects and tasks through the client portal.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* SECTION 1.5: Allowed Request Types */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                            <Tag className="w-5 h-5 mr-2 text-purple-600" />
                            Allowed Request Types
                          </h4>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Select which request types this client can submit through their portal
                          </p>
                        </div>
                        {!allowedRequestTypes.usesAll && (
                          <button
                            onClick={handleResetToAllRequestTypes}
                            className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                          >
                            Reset to All Types
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="p-6">
                      {allowedRequestTypes.companyTypes.length === 0 ? (
                        <div className="text-center py-6 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                          <Info className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                          <p className="text-sm text-yellow-800 dark:text-yellow-200">
                            No request types configured at the company level.
                          </p>
                          <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                            Configure request types in Settings → Client Portal to enable filtering.
                          </p>
                        </div>
                      ) : (
                        <>
                          {/* Info Banner */}
                          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                            <p className="text-sm text-blue-800 dark:text-blue-200">
                              {allowedRequestTypes.usesAll ? (
                                <>
                                  <strong>Using all company request types.</strong> Uncheck any type below to customize which request types this client can submit.
                                </>
                              ) : (
                                <>
                                  <strong>Using custom selection.</strong> Only checked request types will appear in this client&apos;s portal.
                                </>
                              )}
                            </p>
                          </div>

                          {/* Request Types Checkboxes */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {allowedRequestTypes.companyTypes.map((requestType, idx) => {
                              const isChecked = allowedRequestTypes.usesAll || allowedRequestTypes.allowed.includes(requestType);
                              return (
                                <label
                                  key={idx}
                                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${
                                    isChecked
                                      ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700'
                                      : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:border-purple-300'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => handleAllowedRequestTypeToggle(requestType, e.target.checked)}
                                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                                  />
                                  <span className={`ml-3 text-sm font-medium ${
                                    isChecked 
                                      ? 'text-green-800 dark:text-green-200' 
                                      : 'text-gray-700 dark:text-gray-300'
                                  }`}>
                                    {requestType}
                                  </span>
                                </label>
                              );
                            })}
                          </div>

                          {errors.requestTypes && (
                            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
                              {errors.requestTypes}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* SECTION 2: Portal Customization */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
                        <PencilIcon className="w-5 h-5 mr-2" />
                        Portal Customization
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        Customize the form that clients see in their portal and configure task generation
                      </p>
                    </div>

                    <div className="p-6 space-y-6">
                      {/* Auto Task Generation Toggle */}
                      <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div>
                          <h5 className="text-sm font-medium text-gray-900 dark:text-white">
                            Auto-Generate Tasks
                          </h5>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                            Automatically create tasks when clients submit forms through their portal
                          </p>
                        </div>
                        <button
                          onClick={() => handleToggleAutoGenerateTasks(!portalCustomization.auto_generate_tasks)}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                            portalCustomization.auto_generate_tasks ? 'bg-purple-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              portalCustomization.auto_generate_tasks ? 'translate-x-4' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>

                      {errors.autoTasks && (
                        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
                          {errors.autoTasks}
                        </div>
                      )}

                      {/* Form Builder */}
                      <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between">
                            <div>
                              <h5 className="text-sm font-medium text-gray-900 dark:text-white">
                                Client Form Builder
                              </h5>
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                Design the form that appears in this client's portal
                              </p>
                            </div>
                            <button
                              onClick={handleOpenFormBuilder}
                              className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-purple-600 hover:bg-purple-700"
                            >
                              <PencilIcon className="h-3 w-3 mr-1" />
                              Edit Form
                            </button>
                          </div>
                        </div>

                        {/* Form Preview */}
                        <div className="p-4">
                          {portalCustomization.form_config ? (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-600 dark:text-gray-400">
                                  Current Form: <strong>{portalCustomization.form_config.template_name || 'Custom Form'}</strong>
                                </span>
                                <span className="text-gray-500 dark:text-gray-400">
                                  {portalCustomization.form_config.default_schema?.fields?.length || 
                                   portalCustomization.form_config.custom_schema?.fields?.length || 0} fields
                                </span>
                              </div>
                              
                              {/* Quick Field List */}
                              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                                <h6 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
                                  Form Fields
                                </h6>
                                <div className="space-y-1">
                                  {(portalCustomization.form_config.custom_schema?.fields || 
                                    portalCustomization.form_config.default_schema?.fields || [])
                                    .slice(0, 4).map((field) => (
                                    <div key={field.id} className="flex items-center text-xs">
                                      <span className="w-2 h-2 bg-purple-400 rounded-full mr-2"></span>
                                      <span className="text-gray-700 dark:text-gray-300">{field.label}</span>
                                      {field.required && (
                                        <span className="text-red-500 ml-1">*</span>
                                      )}
                                    </div>
                                  ))}
                                  {(portalCustomization.form_config.custom_schema?.fields || 
                                    portalCustomization.form_config.default_schema?.fields || []).length > 4 && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      +{(portalCustomization.form_config.custom_schema?.fields || 
                                         portalCustomization.form_config.default_schema?.fields || []).length - 4} more fields
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Conditional Rules Info */}
                              {(portalCustomization.form_config.custom_rules || 
                                portalCustomization.form_config.default_rules || []).length > 0 && (
                                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-2">
                                  <div className="flex items-center text-xs">
                                    <div className="flex-shrink-0">
                                      <svg className="h-3 w-3 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                    <div className="ml-2">
                                      <span className="font-medium text-yellow-800 dark:text-yellow-200">
                                        Conditional Logic: {(portalCustomization.form_config.custom_rules || 
                                                           portalCustomization.form_config.default_rules || []).length} rule(s) active
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-6">
                              <DocumentTextIcon className="mx-auto h-8 w-8 text-gray-400" />
                              <h6 className="mt-2 text-xs font-medium text-gray-900 dark:text-white">
                                No Custom Form
                              </h6>
                              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                                This client uses the default form template. Click "Edit Form" to customize it.
                              </p>
                            </div>
                          )}
                        </div>

                        {errors.formBuilder && (
                          <div className="p-3 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800 text-xs text-red-600 dark:text-red-400">
                            {errors.formBuilder}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* AI Task Generation Tab */}
              {activeTab === 'ai-tasks' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">AI Task Generation Settings</h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                      Configure how AI-generated tasks are handled for this client. These settings override company defaults.
                    </p>
                  </div>

                  {errors.aiTasks && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
                      {errors.aiTasks}
                    </div>
                  )}

                  {/* Company Defaults Info */}
                  {companyAiSettings && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2 flex items-center">
                        <Settings className="w-4 h-4 mr-2" />
                        Company Default Settings
                      </h4>
                      <div className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                        <div>• Require approval for all tasks: {companyAiSettings.require_approval_for_all ? 'Yes' : 'No'}</div>
                        <div>• Auto-approve high confidence: {companyAiSettings.auto_approve_high_confidence ? 'Yes' : 'No'}</div>
                        <div>• Confidence threshold: {Math.round((companyAiSettings.auto_approve_threshold || 0.85) * 100)}%</div>
                        <div>• Email notifications: {companyAiSettings.notification_preferences?.email_on_generation ? 'Enabled' : 'Disabled'}</div>
                      </div>
                    </div>
                  )}

                  {/* Override Company Settings Toggle */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                          Override Company Settings
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          Use client-specific AI task generation settings instead of company defaults
                        </p>
                      </div>
                      <button
                        onClick={() => handleAiTaskSettingsChange('inherit_company_settings', !aiTaskSettings.inherit_company_settings)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                          !aiTaskSettings.inherit_company_settings ? 'bg-purple-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            !aiTaskSettings.inherit_company_settings ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Client-Specific Settings (only show when overriding) */}
                    {!aiTaskSettings.inherit_company_settings && (
                      <div className="space-y-6 border-t border-gray-200 dark:border-gray-700 pt-6">
                        {/* Approval Settings */}
                        <div className="space-y-4">
                          <h5 className="font-medium text-gray-900 dark:text-white">Approval Workflow</h5>
                          
                          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <div>
                              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Require approval for all AI-generated tasks
                              </label>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                When enabled, all AI-generated tasks will need manual approval before being created
                              </p>
                            </div>
                            <button
                              onClick={() => handleAiTaskSettingsChange('require_approval_for_all', !aiTaskSettings.require_approval_for_all)}
                              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                                aiTaskSettings.require_approval_for_all ? 'bg-purple-600' : 'bg-gray-200'
                              }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  aiTaskSettings.require_approval_for_all ? 'translate-x-4' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </div>

                          {/* Auto-approve settings (only show if not requiring approval for all) */}
                          {!aiTaskSettings.require_approval_for_all && (
                            <>
                              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                <div>
                                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Auto-approve high confidence tasks
                                  </label>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Automatically approve tasks that meet the confidence threshold
                                  </p>
                                </div>
                                <button
                                  onClick={() => handleAiTaskSettingsChange('auto_approve_high_confidence', !aiTaskSettings.auto_approve_high_confidence)}
                                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                                    aiTaskSettings.auto_approve_high_confidence ? 'bg-purple-600' : 'bg-gray-200'
                                  }`}
                                >
                                  <span
                                    className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                      aiTaskSettings.auto_approve_high_confidence ? 'translate-x-4' : 'translate-x-0'
                                    }`}
                                  />
                                </button>
                              </div>

                              {/* Confidence Threshold */}
                              {aiTaskSettings.auto_approve_high_confidence && (
                                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Confidence Threshold ({Math.round(aiTaskSettings.auto_approve_threshold * 100)}%)
                                  </label>
                                  <input
                                    type="range"
                                    min="0.5"
                                    max="1.0"
                                    step="0.05"
                                    value={aiTaskSettings.auto_approve_threshold}
                                    onChange={(e) => handleAiTaskSettingsChange('auto_approve_threshold', parseFloat(e.target.value))}
                                    className="w-full"
                                  />
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Tasks with confidence above this threshold will be auto-approved
                                  </p>
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Notification Settings */}
                        <div className="space-y-4">
                          <h5 className="font-medium text-gray-900 dark:text-white">Email Notifications</h5>
                          
                          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Notify when tasks are generated
                            </label>
                            <button
                              onClick={() => handleAiTaskNotificationChange('email_on_generation', !aiTaskSettings.notification_preferences.email_on_generation)}
                              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                                aiTaskSettings.notification_preferences.email_on_generation ? 'bg-purple-600' : 'bg-gray-200'
                              }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  aiTaskSettings.notification_preferences.email_on_generation ? 'translate-x-4' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </div>

                          <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Notify when approval is needed
                            </label>
                            <button
                              onClick={() => handleAiTaskNotificationChange('email_on_approval_needed', !aiTaskSettings.notification_preferences.email_on_approval_needed)}
                              className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                                aiTaskSettings.notification_preferences.email_on_approval_needed ? 'bg-purple-600' : 'bg-gray-200'
                              }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  aiTaskSettings.notification_preferences.email_on_approval_needed ? 'translate-x-4' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </div>
                        </div>

                        {/* Save Button for AI Settings */}
                        <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                          <button
                            onClick={saveAiTaskSettings}
                            disabled={saving}
                            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <Save className="w-4 h-4" />
                            <span>{saving ? 'Saving...' : 'Save AI Settings'}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Data Tables Tab */}
              {activeTab === 'data-tables' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Data Tables</h3>
                    <p className="text-sm text-gray-600">Turn data tables on/off for this client and view core table status. Ingestion configuration is managed internally.</p>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-lg divide-y">
                    {dataTables.map((t) => {
                      const key = String(t.id);
                      const cfg = dataTableConfigs[key] || {};
                      return (
                        <div key={key} className="p-4 space-y-3">
                          <div className="flex items-center justify-between gap-4">
                            <div>
                              <div className="font-medium text-gray-900">{t.name}</div>
                              <div className="text-xs text-gray-600">{t.row_count || 0} rows · Last updated {t.updated_at ? new Date(t.updated_at).toLocaleString() : '—'}</div>
                              {t.last_error && <div className="text-xs text-red-600 mt-1">Last error: {t.last_error}</div>}
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="flex items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={!!cfg.is_subscribed}
                                  onChange={(e) => updateDataTableConfig(key, { is_subscribed: e.target.checked, is_live: e.target.checked })}
                                />
                                Enabled
                              </label>
                              <button onClick={() => refreshDataTable(key)} className="px-3 py-1 text-xs border rounded hover:bg-gray-50">Refresh</button>
                              <button onClick={() => loadDataTablePreview(key)} className="px-3 py-1 text-xs border rounded hover:bg-gray-50">View items</button>
                            </div>
                          </div>

                          <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded p-2">
                            Core view shown to clients: Name/Label, URL, Status, and key category fields.
                          </div>

                          {dataTablePreviews[key]?.rows?.length > 0 && (
                            <div className="overflow-x-auto border rounded">
                              <table className="min-w-full text-xs">
                                <thead className="bg-gray-50">
                                  <tr>
                                    {(dataTablePreviews[key].columns || []).map((c) => (
                                      <th key={c} className="px-2 py-1 text-left font-medium text-gray-700">{c}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="divide-y">
                                  {dataTablePreviews[key].rows.slice(0, 12).map((r, idx) => (
                                    <tr key={idx}>
                                      {(dataTablePreviews[key].columns || []).map((c) => (
                                        <td key={`${idx}-${c}`} className="px-2 py-1 text-gray-700">{String(r[c] ?? '')}</td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {isSeoTable(t.name) && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs text-gray-600 mb-1">SEO Provider API Key (BYO)</label>
                                <input
                                  type="password"
                                  value={(cfg.method_config || {}).api_key || ''}
                                  onChange={(e) => updateDataTableConfig(key, {
                                    method_config: { ...(cfg.method_config || {}), api_key: e.target.value }
                                  })}
                                  className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                                  placeholder="Enter your Ahrefs/SEMrush key"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {dataTables.length === 0 && (
                      <div className="p-6 text-sm text-gray-500">No data tables found yet.</div>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={saveDataTables}
                      disabled={savingDataTables}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                    >
                      {savingDataTables ? 'Saving...' : 'Save Data Table Settings'}
                    </button>
                  </div>
                </div>
              )}

              {/* Team & Contacts Tab */}
              {activeTab === 'contacts' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Team & Contacts</h3>
                      <p className="text-gray-600">Manage contacts within the client organization</p>
                    </div>
                    <button
                      onClick={addContact}
                      className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Add Contact</span>
                    </button>
                  </div>

                  {teamContacts.length > 0 ? (
                    <div className="space-y-4">
                      {teamContacts.map((contact, index) => (
                        <div key={contact.id} className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-start justify-between mb-4">
                            <h4 className="text-lg font-medium text-gray-900">
                              Contact {index + 1}
                              {contact.is_primary && (
                                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  Primary
                                </span>
                              )}
                            </h4>
                            <button
                              onClick={() => removeContact(contact.id)}
                              className="text-red-600 hover:text-red-700 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                              <input
                                type="text"
                                value={contact.name}
                                onChange={(e) => updateContact(contact.id, 'name', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="Contact name"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                              <input
                                type="email"
                                value={contact.email}
                                onChange={(e) => updateContact(contact.id, 'email', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="contact@example.com"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                              <input
                                type="tel"
                                value={contact.phone}
                                onChange={(e) => updateContact(contact.id, 'phone', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="+1 (555) 123-4567"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                              <input
                                type="text"
                                value={contact.job_title}
                                onChange={(e) => updateContact(contact.id, 'job_title', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="Position"
                              />
                            </div>

                            <div className="md:col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                              <input
                                type="text"
                                value={contact.department}
                                onChange={(e) => updateContact(contact.id, 'department', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="Department"
                              />
                            </div>
                          </div>

                          <div className="mt-4 space-y-3">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                id={`primary-${contact.id}`}
                                checked={contact.is_primary}
                                onChange={(e) => {
                                  // If setting as primary, unset others
                                  if (e.target.checked) {
                                    setTeamContacts(prev => prev.map(c => ({
                                      ...c,
                                      is_primary: c.id === contact.id
                                    })));
                                  } else {
                                    updateContact(contact.id, 'is_primary', false);
                                  }
                                }}
                                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                              />
                              <label htmlFor={`primary-${contact.id}`} className="ml-2 block text-sm text-gray-700">
                                Primary contact
                              </label>
                            </div>
                            
                            {contact.email && (
                              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center space-x-2">
                                  <Lock className="w-4 h-4 text-gray-400" />
                                  <span className="text-sm text-gray-700">Client Portal Access</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    // Toggle portal access
                                    const hasAccess = contact.has_portal_access;
                                    try {
                                      const response = await api.post(
                                        `/clients/${client.id}/contacts/${contact.id}/toggle-portal-access`,
                                        { grant_access: !hasAccess }
                                      );
                                      
                                      if (response.data.success) {
                                        // Update local state
                                        setTeamContacts(prev => prev.map(c =>
                                          c.id === contact.id ? { ...c, has_portal_access: !hasAccess, portal_enabled: !hasAccess } : c
                                        ));
                                        setSaveSuccess(true);
                                        setTimeout(() => setSaveSuccess(false), 3000);
                                      }
                                    } catch (error) {
                                      console.error('Error toggling portal access:', error);
                                      setErrors({ portal: 'Failed to update portal access' });
                                    }
                                  }}
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                                    contact.has_portal_access ? 'bg-purple-600' : 'bg-gray-300'
                                  }`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                      contact.has_portal_access ? 'translate-x-6' : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">No Contacts</h3>
                      <p className="text-gray-600 mb-4">
                        Add contacts to manage multiple people within this client organization.
                      </p>
                      <button
                        onClick={addContact}
                        className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Contact
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Recurring Tasks Tab */}
              {activeTab === 'recurring' && (
                <div className="space-y-6">
                  <RecurringTemplatesList 
                    context="client"
                    contextId={client.id}
                    contextName={client.name}
                  />
                </div>
              )}
          </div>
          
          {/* Footer */}
          {activeTab !== 'recurring' && (
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  * Required fields. Changes will be applied to the client profile.
                </p>
                
                <div className="flex items-center space-x-3">
                  {hasChanges() && (
                    <button
                      onClick={handleReset}
                      className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <RotateCcw className="w-4 h-4" />
                      <span>Reset</span>
                    </button>
                  )}
                  
                  <button
                    onClick={handleSave}
                    disabled={saving || !hasChanges()}
                    className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    <Save className="w-4 h-4" />
                    <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Form Builder Modal */}
      {showFormBuilder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-7xl h-5/6 flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Customize Form for {client.name}
              </h3>
              <button
                onClick={() => setShowFormBuilder(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <FormBuilder
                initialTemplate={portalCustomization.form_config}
                clientId={client.id}
                onSave={handleSaveClientForm}
                onCancel={() => setShowFormBuilder(false)}
                mode="client-customization"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSettings;
